// src/pages/PagesUtilisateurs/DashboardUsers/modules/FavoriteTechniquesDisplay.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { FaStar } from "react-icons/fa";

import { getUserFavorites } from "../../../../services/usersTechniquesServices";
import { getAllTechniques } from "../../../../services/techniquesServices";

import { toAbsoluteTechniquePhotoUrl } from "../../../../services/techniquesPhotoServices";

import DashboardEmptyState from "../ui/DashboardEmptyState";

import {
  getTechniqueId,
  uniqueIds,
} from "../utils/dashboardUser.utils";

export default function FavoriteTechniquesDisplay({
  userId,
  onCounts,
}) {
  const [techniques, setTechniques] =
    useState([]);

  const [favoriteIds, setFavoriteIds] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!userId) return;

      try {
        setLoading(true);

        const [
          favoritesData,
          techniquesData,
        ] = await Promise.all([
          getUserFavorites(
            userId,
          ).catch(() => []),

          getAllTechniques().catch(
            () => [],
          ),
        ]);

        if (!mounted) return;

        const ids = uniqueIds(
          (
            Array.isArray(
              favoritesData,
            )
              ? favoritesData
              : []
          ).map(getTechniqueId),
        );

        setFavoriteIds(ids);

        setTechniques(
          Array.isArray(
            techniquesData,
          )
            ? techniquesData
            : [],
        );

        onCounts?.({
          total: ids.length,
        });
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [userId, onCounts]);

  const favorites = useMemo(() => {
    const map = new Map(
      techniques.map((technique) => [
        Number(technique.id),
        technique,
      ]),
    );

    return favoriteIds
      .map((id) =>
        map.get(Number(id)),
      )
      .filter(Boolean);
  }, [
    techniques,
    favoriteIds,
  ]);

  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({
          length: 6,
        }).map((_, index) => (
          <div
            key={index}
            className="h-28 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (!favorites.length) {
    return (
      <DashboardEmptyState
        icon={FaStar}
        title="Aucune technique favorite"
        description="Ajoutez des techniques à vos favoris depuis la bibliothèque technique."
      />
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {favorites.map((technique) => (
        <article
          key={technique.id}
          className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-3 transition hover:border-slate-300 hover:shadow-sm dark:border-slate-800 dark:bg-slate-900"
        >
          <img
            src={
              toAbsoluteTechniquePhotoUrl(
                technique.image1_url,
              ) ||
              "/img/no-image.jpg"
            }
            alt={
              technique.nom ||
              "Technique"
            }
            className="h-20 w-20 shrink-0 rounded-xl object-cover"
            onError={(event) => {
              event.currentTarget.src =
                "/img/no-image.jpg";
            }}
          />

          <div className="min-w-0">
            <h4 className="font-bold text-slate-950 dark:text-white">
              {technique.nom || "—"}
            </h4>

            {technique.traduction ? (
              <p className="mt-0.5 text-xs italic text-slate-400">
                {
                  technique.traduction
                }
              </p>
            ) : null}

            <div className="mt-2 flex flex-wrap gap-1.5">
              {technique.famille ? (
                <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {technique.famille}
                </span>
              ) : null}

              {technique.ceinture ? (
                <span className="rounded-full bg-blue-50 px-2 py-1 text-[10px] font-bold text-[#013E8E] dark:bg-blue-500/10 dark:text-blue-300">
                  {technique.ceinture}
                </span>
              ) : null}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}